import type { NextWebVitalsMetric } from "next/app";

/**
 * First-party replacement for the GTM `web-vitals-ga4` custom HTML tag
 * (container GTM-WC6MPKS, tag 832). That tag loads `web-vitals` from unpkg
 * and pushes `coreWebVitals` events that tag 159 forwards to GA4. This module
 * builds the SAME payload from the metric Next.js already hands to
 * `reportWebVitals` in `pages/_app.js`, so the GTM collectors can be paused
 * without changing what GA4 receives. WHO-5479 S4.
 */
export type WebVitalsMeasurement = {
  name: string;
  id: string;
  value: number;
  delta: number;
  rating: string;
  valueRounded: number;
  deltaRounded: number;
  dbg_target: string;
  dbg_target2: string;
  dbg_event: string;
  dbg_timing: string;
  event_time: number | string;
};

export type CoreWebVitalsEvent = {
  event: "coreWebVitals";
  webVitalsMeasurement: WebVitalsMeasurement;
};

/* Next's public type stops at `attribution?: { [key: string]: unknown }`; the
   fields below are the ones the web-vitals attribution build actually sets
   and the GTM tag actually read. Everything stays optional and is only used
   when present. */
type ReportedMetric = NextWebVitalsMetric & {
  delta?: number;
  rating?: string;
  attribution?: {
    largestShiftTarget?: string;
    largestShiftTime?: number;
    element?: string;
    eventTarget?: string;
    eventType?: string;
    eventTime?: number;
    loadState?: string;
    lcpEntry?: { startTime?: number };
    [key: string]: unknown;
  };
};

export const FIRST_PARTY_WEB_VITALS_FLAG = "NEXT_PUBLIC_FIRST_PARTY_WEB_VITALS";

const TARGET_SPLIT_AT = 100;

/* The literal string "true" and nothing else; a missing or empty value keeps
   the push off, which is the state master ships in. */
export const isFirstPartyWebVitalsEnabled = (
  value: string | undefined = process.env.NEXT_PUBLIC_FIRST_PARTY_WEB_VITALS
) => value === "true";

/* GA4 custom metrics take integers; CLS is a unitless fraction so the GTM tag
   scaled it by 1000 before rounding. Kept identical here. */
const roundForGa4 = (name: string, value: number) =>
  Math.round(name === "CLS" ? value * 1000 : value);

export const toWebVitalsMeasurement = (
  metric: NextWebVitalsMetric
): WebVitalsMeasurement => {
  const { name, id, value } = metric;
  const { delta, rating, attribution } = metric as ReportedMetric;
  const resolvedDelta = typeof delta === "number" ? delta : value;

  const target =
    attribution?.largestShiftTarget ||
    attribution?.element ||
    attribution?.eventTarget ||
    "(not set)";

  const eventTime =
    attribution?.largestShiftTime ??
    attribution?.lcpEntry?.startTime ??
    attribution?.eventTime ??
    "";

  return {
    name,
    id,
    value,
    delta: resolvedDelta,
    rating: rating ?? "",
    valueRounded: roundForGa4(name, value),
    deltaRounded: roundForGa4(name, resolvedDelta),
    dbg_target: target.slice(0, TARGET_SPLIT_AT),
    dbg_target2: target.slice(TARGET_SPLIT_AT),
    dbg_event: attribution?.eventType || "",
    dbg_timing: attribution?.loadState || "",
    event_time: eventTime,
  };
};

/* Next also reports its own `custom` metrics (hydration, route change). The
   GTM tag never saw those, so they are skipped to keep the GA4 stream equal. */
export const pushWebVitalToDataLayer = (metric: NextWebVitalsMetric) => {
  if (metric.label !== "web-vital" || typeof window === "undefined") {
    return;
  }

  window.dataLayer = window.dataLayer || [];

  const payload: CoreWebVitalsEvent = {
    event: "coreWebVitals",
    webVitalsMeasurement: toWebVitalsMeasurement(metric),
  };

  window.dataLayer.push(payload);
};
